import { useMutation } from '@tanstack/react-query';
import { updateActivity } from 'api/contacts/activities';
import { Archived } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/archived';
import CampaignJoined from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/campaign-joined';
import { EngageEmail } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/engage-email';
import { EngageLetter } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/engage-letter';
import { ExternalEmail } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/external-email';
import ManualItem from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/manual';
import {
    PledgeCreated,
    PledgeOffSchedule,
    PledgeTransactionSucceeded,
} from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/pledge';
import RecurringPlanCreated from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/recurring-plan-created';
import SignupFormSubmitted from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/signup-form-submitted';
import { SoftCredit } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/soft-credit';
import { SubscriptionActivity } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/subscription';
import { TicketIssued } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/ticket-issued';
import { TransactionActivity } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/transaction-activity/TransactionActivity';
import { Unarchived } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/unarchived';
import { AxiosResponse } from 'axios';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import {
    ActivityItem,
    ActivityWithResource,
    ActivityWithSource,
    ArchivedActivity,
    Contact,
    PledgeCreatedActivity,
    PledgeOffScheduleActivity,
    PledgeTransactionSucceededActivity,
    SoftCreditActivity,
    UnarchivedActivity,
} from 'types/domain';

type Activity =
    | ActivityItem
    | ActivityWithSource
    | ArchivedActivity
    | UnarchivedActivity
    | PledgeCreatedActivity
    | PledgeTransactionSucceededActivity
    | PledgeOffScheduleActivity
    | SoftCreditActivity;

type ActivityComponentProps = {
    item: Activity;
    contact: Contact | null;
    saveNote: (data: { note: string }) => Promise<AxiosResponse>;
};

const getTimelineComp = (item: Activity) => {
    switch (item.type) {
        case 'letter':
            return EngageLetter;
        case 'sms':
        case 'email':
            if ((item as ActivityWithSource).source === 'manual') {
                return ManualItem;
            }
            if (
                (item as ActivityWithResource).resource_type === 'message_delivery' ||
                (item as ActivityWithResource).resource_type === 'mail_piece'
            ) {
                return EngageEmail;
            }
            return ExternalEmail;
        case 'phone_call':
        case 'note':
        case 'meeting':
        case 'completed_task':
        case 'volunteer_activity':
            return ManualItem;
        case 'ticket.issued':
            return TicketIssued;
        case 'transaction.recieved':
        case 'transaction.succeeded':
            return TransactionActivity;
        case 'recurring_plan.created':
            return RecurringPlanCreated;
        case 'campaign.joined':
            return CampaignJoined;
        case 'signup_form.submitted':
            return SignupFormSubmitted;
        case 'archived':
            return Archived;
        case 'unarchived':
            return Unarchived;
        case 'pledge.created':
            return PledgeCreated;
        case 'pledge.transaction.succeeded':
            return PledgeTransactionSucceeded;
        case 'pledge.installment.off_schedule':
            return PledgeOffSchedule;
        case 'soft_credited':
            return SoftCredit;
        case 'email.subscribed':
        case 'email.unsubscribed':
        case 'sms.subscribed':
        case 'sms.unsubscribed':
            return SubscriptionActivity;
        default:
            return null;
    }
};

const TimelineItem = ({
    item,
    isLast,
    fetchTimeLine,
}: {
    item: Activity;
    isLast: boolean;
    fetchTimeLine: () => void;
}) => {
    const { accountId, timezone } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const Comp = getTimelineComp(item) as (props: ActivityComponentProps) => JSX.Element;

    const { mutateAsync: saveNote } = useMutation({
        mutationFn: (data: { note: string }) =>
            updateActivity(accountId, contactId, {
                activityId: item.id,
                data,
            }),
        onSuccess: () => {
            fetchTimeLine();
        },
    });

    if (!Comp) return null;

    const date = isoDateFromServer(item.occurred_at)
        .setZone(timezone)
        .toLocaleString(DateTime.DATE_MED);

    return (
        <div className={'grow-1 relative mb-8 flex flex-col justify-between'}>
            <div className={'flex flex-col gap-y-3'}>
                <p className="text-sm font-medium">{date}</p>
                <Comp key={item.id} item={item} contact={contact} saveNote={saveNote} />
            </div>
            {!isLast && (
                <div className={'absolute bottom-[-24px] left-[26px] h-6 w-px bg-zinc-200'} />
            )}
        </div>
    );
};

export { TimelineItem };
